import React, { useState, useEffect, useCallback, useRef } from 'react';
import './QAValidationPage.css';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/evalService';

/*
 * REKKIE Eval & Test-Suites — QA Validation tab.
 * Talks to the backend at /api/eval via src/services/evalService.js.
 * Product spec: plans/eval-test-suites/product.md
 */

const STATUS = {
  passed: { label: 'Passed', cls: 'pass', mark: '✓' },
  failed: { label: 'Failed', cls: 'fail', mark: '✕' },
  skipped: { label: 'Skipped', cls: 'skip', mark: '–' },
  not_started: { label: 'Not started', cls: 'none', mark: '○' },
};

const FILTER_LABELS = { all: 'All', in_progress: 'In progress', complete: 'Complete' };

const fmtDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const passRateOf = (c) => {
  const judged = c.passed + c.failed;
  return judged > 0 ? Math.round((c.passed / judged) * 100) : null;
};

const caseStatus = (c) => (c.result ? c.result.status : 'not_started');

/** Aggregate counts across all cases of an eval-detail object. */
function detailStats(detail) {
  const counts = { passed: 0, failed: 0, skipped: 0, not_started: 0 };
  (detail.suites || []).forEach((s) => s.cases.forEach((c) => {
    counts[caseStatus(c)] += 1;
  }));
  const total = counts.passed + counts.failed + counts.skipped + counts.not_started;
  return { counts, total, done: counts.passed + counts.failed + counts.skipped };
}

// ---------------------------------------------------------------- primitives

const avatarColor = (key) => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 360;
  return `hsl(${h}, 42%, 46%)`;
};

const initialsOf = (name) => {
  const parts = (name || '?').trim().split(/\s+/);
  return ((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
};

const Avatar = ({ person, size = 26 }) => {
  if (!person) return null;
  const name = person.name || person.uid || '?';
  return (
    <span
      className="qa-avatar"
      title={name}
      style={{
        width: size,
        height: size,
        background: avatarColor(person.uid || name),
        fontSize: size * 0.4,
      }}
    >
      {initialsOf(name)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const meta = STATUS[status] || STATUS.not_started;
  return <span className={`qa-badge qa-badge-${meta.cls}`}>{meta.label}</span>;
};

const EvalStatusBadge = ({ status }) => {
  const done = status === 'complete';
  return (
    <span className={`qa-badge qa-badge-${done ? 'done' : 'progress'}`}>
      {done ? 'Complete' : 'In progress'}
    </span>
  );
};

const ProgressBar = ({ counts, total }) => {
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
  return (
    <div className="qa-progress">
      <div className="qa-progress-seg qa-seg-pass" style={{ width: `${pct(counts.passed)}%` }} />
      <div className="qa-progress-seg qa-seg-fail" style={{ width: `${pct(counts.failed)}%` }} />
      <div className="qa-progress-seg qa-seg-skip" style={{ width: `${pct(counts.skipped)}%` }} />
    </div>
  );
};

const Spinner = ({ label }) => <div className="qa-loading">{label || 'Loading…'}</div>;
const ErrorNote = ({ message }) => <div className="qa-error">{message}</div>;

// ---------------------------------------------------------------- media

const MEDIA_META = {
  screenshot: { icon: '🖼', addLabel: '+ Add screenshot' },
  video: { icon: '🎬', addLabel: '+ Add video' },
};

/**
 * Reference / evidence media list. Read-only renders chips with links; editable
 * adds a real file picker. `onAddFile(file)` does the upload and is awaited so
 * the button can show a busy state.
 */
const MediaList = ({ label, items, kind, editable = false, onAddFile, onRemove }) => {
  const meta = MEDIA_META[kind];
  const list = items || [];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  if (!editable && list.length === 0) return null;

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      await onAddFile(file);
    } catch (err) {
      // the caller surfaces the error in its own state
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qa-media-block">
      {label && <span className="qa-media-label">{label}</span>}
      <div className="qa-media">
        {list.map((item, i) => (
          <div className={`qa-media-chip qa-media-${kind}`} key={item.path || i}>
            <span className="qa-media-icon">{meta.icon}</span>
            {item.url ? (
              <a className="qa-media-name" href={item.url} target="_blank" rel="noreferrer">
                {item.filename}
              </a>
            ) : (
              <span className="qa-media-name">{item.filename}</span>
            )}
            {editable && <button className="qa-media-x" onClick={() => onRemove(i)}>✕</button>}
          </div>
        ))}
        {editable && (
          <>
            <input
              ref={inputRef}
              type="file"
              style={{ display: 'none' }}
              accept={kind === 'video' ? 'video/*' : 'image/*'}
              onChange={onFile}
            />
            <button
              className="qa-media-add"
              disabled={busy}
              onClick={() => inputRef.current && inputRef.current.click()}
            >
              {busy ? 'Uploading…' : meta.addLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- markdown

// Minimal Markdown renderer for step descriptions: paragraphs, bullet lists,
// bold, italic, inline code, and links. Builds React nodes (no innerHTML).
function renderInline(text) {
  const nodes = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let key = 0;
  let m = pattern.exec(text);
  while (m) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    key += 1;
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      nodes.push(<code key={key}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('[')) {
      const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      nodes.push(<a key={key} href={lm[2]} target="_blank" rel="noreferrer">{lm[1]}</a>);
    } else {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
    m = pattern.exec(text);
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const Markdown = ({ text }) => {
  if (!text || !text.trim()) return null;
  const blocks = [];
  let list = null;
  text.split('\n').forEach((line) => {
    const t = line.trim();
    if (t.startsWith('- ')) {
      if (!list) list = [];
      list.push(t.slice(2));
      return;
    }
    if (list) { blocks.push({ kind: 'ul', items: list }); list = null; }
    if (t !== '') blocks.push({ kind: 'p', text: t });
  });
  if (list) blocks.push({ kind: 'ul', items: list });
  return (
    <div className="qa-md">
      {blocks.map((b, i) => (b.kind === 'ul'
        ? <ul key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>
        : <p key={i}>{renderInline(b.text)}</p>))}
    </div>
  );
};

// ---------------------------------------------------------------- kickoff modal

const KickoffModal = ({ appVersions, gogglesVersions, onClose, onStart }) => {
  const [appVersion, setAppVersion] = useState(appVersions[0] || '');
  const [gogglesVersion, setGogglesVersion] = useState(gogglesVersions[0] || '');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const start = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onStart({
        appVersion,
        gogglesVersion,
        label: label.trim() || `${appVersion} / ${gogglesVersion} eval`,
      });
    } catch (e) {
      setErr(e.message || 'Could not start the eval');
      setBusy(false);
    }
  };

  return (
    <div className="qa-overlay" onClick={onClose}>
      <div className="qa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qa-modal-head">
          <h3>Start a new eval</h3>
          <button className="qa-x" onClick={onClose}>✕</button>
        </div>
        <p className="qa-modal-sub">
          An eval is a full QA pass. It includes every test suite — you choose which cases to run.
        </p>

        <label className="qa-field">
          <span>App version</span>
          <select value={appVersion} onChange={(e) => setAppVersion(e.target.value)}>
            {appVersions.length === 0 && <option value="">No versions configured</option>}
            {appVersions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>

        <label className="qa-field">
          <span>Goggles version</span>
          <select value={gogglesVersion} onChange={(e) => setGogglesVersion(e.target.value)}>
            {gogglesVersions.length === 0 && <option value="">No versions configured</option>}
            {gogglesVersions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>

        <label className="qa-field">
          <span>Label <em>(optional)</em></span>
          <input
            type="text"
            placeholder="e.g. Pre-release smoke"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <div className="qa-note-banner">
          This eval will pin the current version of every test suite. Later edits to a
          suite will not change this eval.
        </div>

        {err && <ErrorNote message={err} />}

        <div className="qa-modal-actions">
          <button className="qa-btn" onClick={onClose}>Cancel</button>
          <button
            className="qa-btn qa-btn-primary"
            disabled={busy || !appVersion || !gogglesVersion}
            onClick={start}
          >
            {busy ? 'Starting…' : 'Start eval'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- case runner

function defaultStepResults(result) {
  const out = {};
  if (result && Array.isArray(result.steps)) {
    result.steps.forEach((sr) => { out[sr.stepId] = sr.result; });
  }
  return out;
}

const CaseRunnerPanel = ({ evalId, suite, testCase, recordedAs, onClose, onSave }) => {
  const result = testCase.result;
  const [stepResults, setStepResults] = useState(() => defaultStepResults(result));
  const [verdict, setVerdict] = useState(result ? result.status : '');
  const [note, setNote] = useState(result ? result.note || '' : '');
  const [shots, setShots] = useState(result ? [...result.evidence] : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const setStep = (stepId, value) => {
    setStepResults((prev) => ({ ...prev, [stepId]: prev[stepId] === value ? '' : value }));
  };

  const noteMissing = verdict === 'failed' && note.trim() === '';
  const canSave = verdict !== '' && !noteMissing && !busy;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSave({
        status: verdict,
        note: note.trim(),
        steps: testCase.steps
          .map((st) => ({ stepId: st.id, result: stepResults[st.id] }))
          .filter((x) => x.result === 'pass' || x.result === 'fail'),
        evidence: shots.map((m) => ({ path: m.path, filename: m.filename })),
      });
    } catch (e) {
      setErr(e.message || 'Could not save the result');
      setBusy(false);
    }
  };

  return (
    <div className="qa-overlay qa-overlay-right" onClick={onClose}>
      <div className="qa-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="qa-drawer-head">
          <div>
            <div className="qa-drawer-suite">
              <span>{suite.name}</span>
              <span className="qa-vtag">v{suite.version}</span>
            </div>
            <h3>{testCase.name}</h3>
          </div>
          <button className="qa-x" onClick={onClose}>✕</button>
        </div>

        <div className="qa-drawer-body">
          {testCase.description && <p className="qa-case-desc">{testCase.description}</p>}

          <MediaList label="Reference video" items={testCase.videos} kind="video" />

          <div className="qa-section-label">Steps</div>
          <div className="qa-steps">
            {testCase.steps.map((step, i) => (
              <div className="qa-step" key={step.id}>
                <div className="qa-step-main">
                  <div className="qa-step-n">{i + 1}</div>
                  <div className="qa-step-body">
                    <div className="qa-step-part">
                      <span className="qa-step-label">Action</span>
                      <div className="qa-step-title">{step.title}</div>
                      {step.description && <Markdown text={step.description} />}
                    </div>
                    {step.expected && (
                      <div className="qa-step-part">
                        <span className="qa-step-label qa-step-label-exp">Expected</span>
                        <div className="qa-step-expected">{step.expected}</div>
                      </div>
                    )}
                  </div>
                  <div className="qa-step-toggle">
                    <button
                      className={`qa-tog qa-tog-pass ${stepResults[step.id] === 'pass' ? 'on' : ''}`}
                      onClick={() => setStep(step.id, 'pass')}
                    >
                      ✓
                    </button>
                    <button
                      className={`qa-tog qa-tog-fail ${stepResults[step.id] === 'fail' ? 'on' : ''}`}
                      onClick={() => setStep(step.id, 'fail')}
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <MediaList label="Reference" items={step.screenshots} kind="screenshot" />
              </div>
            ))}
          </div>

          <div className="qa-section-label">Case verdict</div>
          <div className="qa-verdicts">
            {['passed', 'failed', 'skipped'].map((v) => (
              <button
                key={v}
                className={`qa-verdict qa-verdict-${STATUS[v].cls} ${verdict === v ? 'on' : ''}`}
                onClick={() => setVerdict(v)}
              >
                {STATUS[v].mark} {STATUS[v].label}
              </button>
            ))}
          </div>

          <div className="qa-section-label">
            Notes {verdict === 'failed' && <em className="qa-req">required</em>}
          </div>
          <textarea
            className={`qa-textarea ${noteMissing ? 'qa-textarea-err' : ''}`}
            placeholder={verdict === 'failed'
              ? 'Describe what went wrong (required for a failure)…'
              : 'Optional notes about this test…'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="qa-section-label">Evidence screenshots <em>(optional)</em></div>
          <MediaList
            kind="screenshot"
            editable
            items={shots}
            onAddFile={async (file) => {
              try {
                const m = await api.uploadEvidence(evalId, testCase.id, file);
                setShots((prev) => [...prev, m]);
              } catch (e) {
                setErr(e.message || 'Upload failed');
              }
            }}
            onRemove={(i) => setShots(shots.filter((_, idx) => idx !== i))}
          />

          {err && <ErrorNote message={err} />}
        </div>

        <div className="qa-drawer-foot">
          <span className="qa-recorded">Recorded as {recordedAs}</span>
          <div className="qa-modal-actions">
            <button className="qa-btn" onClick={onClose}>Cancel</button>
            <button className="qa-btn qa-btn-primary" disabled={!canSave} onClick={save}>
              {busy ? 'Saving…' : 'Save result'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- eval list

const EvalListScreen = ({ evals, loading, error, onOpenEval, onStartEval }) => {
  const [filter, setFilter] = useState('all');
  const shown = evals.filter((e) => filter === 'all' || e.status === filter);

  return (
    <div className="qa-screen">
      <div className="qa-toolbar">
        <div className="qa-filters">
          {['all', 'in_progress', 'complete'].map((f) => (
            <button
              key={f}
              className={`qa-chip ${filter === f ? 'qa-chip-on' : ''}`}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f]}
              <span className="qa-chip-n">
                {f === 'all' ? evals.length : evals.filter((e) => e.status === f).length}
              </span>
            </button>
          ))}
        </div>
        <button className="qa-btn qa-btn-primary" onClick={onStartEval}>+ Start new eval</button>
      </div>

      {error && <ErrorNote message={error} />}
      {loading && <Spinner label="Loading evals…" />}

      {!loading && (
        <div className="qa-eval-list">
          {shown.map((run) => {
            const rate = passRateOf(run.counts);
            return (
              <button key={run.id} className="qa-eval-card" onClick={() => onOpenEval(run.id)}>
                <div className="qa-eval-card-top">
                  <div className="qa-vpills">
                    <span className="qa-vpill"><b>App</b>{run.appVersion}</span>
                    <span className="qa-vpill"><b>Goggles</b>{run.gogglesVersion}</span>
                  </div>
                  <EvalStatusBadge status={run.status} />
                </div>
                <div className="qa-eval-card-label">{run.label}</div>
                <ProgressBar counts={run.counts} total={run.total} />
                <div className="qa-eval-card-stats">
                  <span className="qa-mini qa-mini-pass">{run.counts.passed} passed</span>
                  <span className="qa-mini qa-mini-fail">{run.counts.failed} failed</span>
                  <span className="qa-mini qa-mini-skip">{run.counts.skipped} skipped</span>
                  <span className="qa-mini qa-mini-none">{run.counts.notStarted} left</span>
                  <span className="qa-mini-rate">
                    {rate === null ? '—' : `${rate}%`} pass rate
                  </span>
                </div>
                <div className="qa-eval-card-foot">
                  <span className="qa-who">
                    <Avatar person={run.startedBy} />
                    {run.startedBy.name}
                  </span>
                  <span className="qa-muted">Started {fmtDate(run.startedAt)}</span>
                </div>
              </button>
            );
          })}
          {shown.length === 0 && <div className="qa-empty">No evals match this filter.</div>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- eval runner

const EvalRunnerScreen = ({ run, onBack, onOpenCase, onToggleComplete }) => {
  const [expanded, setExpanded] = useState(() => {
    const o = {};
    (run.suites || []).forEach((s) => { o[s.suiteId] = true; });
    return o;
  });
  const st = detailStats(run);
  const rate = passRateOf(st.counts);

  return (
    <div className="qa-screen">
      <button className="qa-back" onClick={onBack}>← All evals</button>

      <div className="qa-runner-head">
        <div className="qa-runner-head-top">
          <div>
            <h2>{run.label}</h2>
            <div className="qa-vpills">
              <span className="qa-vpill"><b>App</b>{run.appVersion}</span>
              <span className="qa-vpill"><b>Goggles</b>{run.gogglesVersion}</span>
              <EvalStatusBadge status={run.status} />
            </div>
          </div>
          <button
            className={`qa-btn ${run.status === 'complete' ? '' : 'qa-btn-primary'}`}
            onClick={onToggleComplete}
          >
            {run.status === 'complete' ? 'Reopen eval' : 'Mark complete'}
          </button>
        </div>

        <div className="qa-runner-summary">
          <div className="qa-sumcard qa-sumcard-pass"><b>{st.counts.passed}</b><span>Passed</span></div>
          <div className="qa-sumcard qa-sumcard-fail"><b>{st.counts.failed}</b><span>Failed</span></div>
          <div className="qa-sumcard qa-sumcard-skip"><b>{st.counts.skipped}</b><span>Skipped</span></div>
          <div className="qa-sumcard qa-sumcard-none">
            <b>{st.counts.not_started}</b><span>Not started</span>
          </div>
          <div className="qa-sumcard qa-sumcard-rate">
            <b>{rate === null ? '—' : `${rate}%`}</b><span>Pass rate</span>
          </div>
        </div>
        <ProgressBar counts={st.counts} total={st.total} />
        <div className="qa-runner-meta">
          Started by {run.startedBy.name} on {fmtDate(run.startedAt)}
          {run.completedBy && ` · Completed by ${run.completedBy.name} on ${fmtDate(run.completedAt)}`}
          {` · ${st.done}/${st.total} cases tested`}
        </div>
      </div>

      {(run.suites || []).map((suite) => {
        const open = expanded[suite.suiteId];
        const done = suite.cases.filter((c) => c.result).length;
        return (
          <div className="qa-suite-block" key={suite.suiteId}>
            <button
              className="qa-suite-bar"
              onClick={() => setExpanded((p) => ({ ...p, [suite.suiteId]: !p[suite.suiteId] }))}
            >
              <span className="qa-suite-caret">{open ? '▾' : '▸'}</span>
              <span className="qa-suite-name">{suite.name}</span>
              <span className="qa-vtag">v{suite.version} pinned</span>
              <span className="qa-suite-count">{done}/{suite.cases.length}</span>
            </button>

            {open && (
              <div className="qa-case-rows">
                {suite.cases.map((tc) => {
                  const status = caseStatus(tc);
                  return (
                    <button
                      key={tc.id}
                      className="qa-case-row"
                      onClick={() => onOpenCase(suite.suiteId, tc.id)}
                    >
                      <span className={`qa-case-mark qa-case-mark-${STATUS[status].cls}`}>
                        {STATUS[status].mark}
                      </span>
                      <span className="qa-case-info">
                        <span className="qa-case-name">{tc.name}</span>
                        {tc.result && tc.result.note && status === 'failed' && (
                          <span className="qa-case-note">{tc.result.note}</span>
                        )}
                      </span>
                      <span className="qa-case-meta">
                        {tc.result && (
                          <>
                            <Avatar person={tc.result.testedBy} size={22} />
                            <span className="qa-muted">{fmtDate(tc.result.testedAt)}</span>
                          </>
                        )}
                        <StatusBadge status={status} />
                      </span>
                      <span className="qa-case-go">›</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------- suites list

const SuitesScreen = ({ suites, loading, error, onOpenSuite, onNewSuite }) => (
  <div className="qa-screen">
    <div className="qa-toolbar">
      <div className="qa-muted">{suites.length} test suites · seeded from config, editable here</div>
      <button className="qa-btn qa-btn-primary" onClick={onNewSuite}>+ New suite</button>
    </div>
    {error && <ErrorNote message={error} />}
    {loading && <Spinner label="Loading suites…" />}
    {!loading && (
      <div className="qa-suite-list">
        {suites.map((suite) => (
          <button key={suite.id} className="qa-suite-card" onClick={() => onOpenSuite(suite.id)}>
            <div className="qa-suite-card-top">
              <span className="qa-suite-card-name">{suite.name}</span>
              <span className="qa-vtag">v{suite.version}</span>
            </div>
            <p className="qa-suite-card-desc">{suite.description || 'No description.'}</p>
            <div className="qa-suite-card-foot">
              <span className="qa-muted">{suite.caseCount} cases · {suite.stepCount} steps</span>
              <span className="qa-muted">Updated {fmtDate(suite.updatedAt)}</span>
            </div>
          </button>
        ))}
        {suites.length === 0 && <div className="qa-empty">No test suites yet.</div>}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------- suite view

const SuiteViewScreen = ({ suite, onBack, onEdit }) => {
  const [expanded, setExpanded] = useState(() => {
    const o = {};
    suite.cases.forEach((c) => { o[c.id] = true; });
    return o;
  });
  const stepCount = suite.cases.reduce((n, c) => n + c.steps.length, 0);

  return (
    <div className="qa-screen">
      <button className="qa-back" onClick={onBack}>← Test suites</button>

      <div className="qa-view-head">
        <div>
          <div className="qa-view-titlerow">
            <h2>{suite.name}</h2>
            <span className="qa-vtag">v{suite.version}</span>
          </div>
          <p className="qa-view-desc">{suite.description || 'No description.'}</p>
          <div className="qa-muted">
            {suite.cases.length} cases · {stepCount} steps · updated {fmtDate(suite.updatedAt)}
          </div>
        </div>
        <button className="qa-btn qa-btn-primary" onClick={onEdit}>Edit suite</button>
      </div>

      {suite.cases.map((tc, ci) => {
        const open = expanded[tc.id];
        return (
          <div className="qa-suite-block" key={tc.id}>
            <button
              className="qa-suite-bar"
              onClick={() => setExpanded((p) => ({ ...p, [tc.id]: !p[tc.id] }))}
            >
              <span className="qa-suite-caret">{open ? '▾' : '▸'}</span>
              <span className="qa-case-idx">Case {ci + 1}</span>
              <span className="qa-suite-name">{tc.name}</span>
              <span className="qa-suite-count">{tc.steps.length} steps</span>
            </button>
            {open && (
              <div className="qa-view-case-body">
                {tc.description && <p className="qa-case-desc">{tc.description}</p>}
                <MediaList label="Reference video" items={tc.videos} kind="video" />
                <div className="qa-steps">
                  {tc.steps.map((step, i) => (
                    <div className="qa-step" key={step.id}>
                      <div className="qa-step-main">
                        <div className="qa-step-n">{i + 1}</div>
                        <div className="qa-step-body">
                          <div className="qa-step-part">
                            <span className="qa-step-label">Action</span>
                            <div className="qa-step-title">{step.title}</div>
                            {step.description && <Markdown text={step.description} />}
                          </div>
                          {step.expected && (
                            <div className="qa-step-part">
                              <span className="qa-step-label qa-step-label-exp">Expected</span>
                              <div className="qa-step-expected">{step.expected}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <MediaList label="Reference" items={step.screenshots} kind="screenshot" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {suite.cases.length === 0 && (
        <div className="qa-empty">This suite has no test cases yet. Use Edit suite to add some.</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- suite editor

const tmpId = () => `tmp-${Math.random().toString(36).slice(2)}`;

const SuiteEditorScreen = ({ suite, onBack, onSave }) => {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(suite)));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const nextVersion = suite.version + 1;

  const patchCase = (caseId, patch) => setDraft((d) => ({
    ...d,
    cases: d.cases.map((c) => (c.id === caseId ? { ...c, ...patch } : c)),
  }));
  const patchStep = (caseId, idx, patch) => setDraft((d) => ({
    ...d,
    cases: d.cases.map((c) => (c.id === caseId
      ? { ...c, steps: c.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }
      : c)),
  }));
  const addStep = (caseId) => setDraft((d) => ({
    ...d,
    cases: d.cases.map((c) => (c.id === caseId
      ? { ...c, steps: [...c.steps, { id: tmpId(), title: '', description: '', expected: '', screenshots: [] }] }
      : c)),
  }));
  const removeStep = (caseId, idx) => setDraft((d) => ({
    ...d,
    cases: d.cases.map((c) => (c.id === caseId
      ? { ...c, steps: c.steps.filter((_, i) => i !== idx) }
      : c)),
  }));
  const addCase = () => setDraft((d) => ({
    ...d,
    cases: [...d.cases, {
      id: tmpId(),
      name: 'New test case',
      description: '',
      videos: [],
      steps: [{ id: tmpId(), title: '', description: '', expected: '', screenshots: [] }],
    }],
  }));
  const removeCase = (caseId) => setDraft((d) => ({
    ...d, cases: d.cases.filter((c) => c.id !== caseId),
  }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(draft);
    } catch (e) {
      setErr(e.message || 'Could not save the suite');
      setSaving(false);
    }
  };

  return (
    <div className="qa-screen">
      <button className="qa-back" onClick={onBack}>← Cancel editing</button>

      <div className="qa-editor-head">
        <input
          className="qa-editor-title"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <textarea
          className="qa-editor-desc"
          placeholder="Suite description…"
          value={draft.description || ''}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
        />
        <div className="qa-note-banner">
          Saving creates <b>version v{nextVersion}</b>. Evals already in progress keep the
          version they pinned — only new evals pick up v{nextVersion}.
        </div>
      </div>

      {draft.cases.map((c, ci) => (
        <div className="qa-edit-case" key={c.id}>
          <div className="qa-edit-case-head">
            <span className="qa-edit-case-n">Case {ci + 1}</span>
            <input
              className="qa-edit-case-name"
              value={c.name}
              onChange={(e) => patchCase(c.id, { name: e.target.value })}
            />
            <button className="qa-btn qa-btn-ghost-danger" onClick={() => removeCase(c.id)}>
              Remove case
            </button>
          </div>
          <input
            className="qa-edit-case-desc"
            placeholder="Case description…"
            value={c.description || ''}
            onChange={(e) => patchCase(c.id, { description: e.target.value })}
          />
          <MediaList
            label="Case reference videos"
            kind="video"
            editable
            items={c.videos}
            onAddFile={async (file) => {
              try {
                const m = await api.uploadSuiteMedia(suite.id, file);
                patchCase(c.id, { videos: [...(c.videos || []), m] });
              } catch (e) {
                setErr(e.message || 'Upload failed');
              }
            }}
            onRemove={(idx) => patchCase(c.id, {
              videos: (c.videos || []).filter((_, k) => k !== idx),
            })}
          />
          <div className="qa-edit-steps">
            {c.steps.map((s, si) => (
              <div className="qa-edit-step" key={s.id}>
                <div className="qa-edit-step-head">
                  <span className="qa-step-n">{si + 1}</span>
                  <span className="qa-step-label">Action — what the tester does</span>
                  <button className="qa-x-small" onClick={() => removeStep(c.id, si)}>✕</button>
                </div>
                <input
                  className="qa-edit-step-title"
                  placeholder="Step title (required)"
                  value={s.title}
                  onChange={(e) => patchStep(c.id, si, { title: e.target.value })}
                />
                <textarea
                  className="qa-edit-step-desc"
                  placeholder="Extra detail, notes, links — Markdown (optional)"
                  value={s.description || ''}
                  onChange={(e) => patchStep(c.id, si, { description: e.target.value })}
                />
                <span className="qa-step-label qa-step-label-exp">Expected — what the tester sees</span>
                <input
                  className="qa-edit-step-exp"
                  placeholder="Expected result"
                  value={s.expected || ''}
                  onChange={(e) => patchStep(c.id, si, { expected: e.target.value })}
                />
                <MediaList
                  label="Step reference screenshots"
                  kind="screenshot"
                  editable
                  items={s.screenshots}
                  onAddFile={async (file) => {
                    try {
                      const m = await api.uploadSuiteMedia(suite.id, file);
                      patchStep(c.id, si, { screenshots: [...(s.screenshots || []), m] });
                    } catch (e) {
                      setErr(e.message || 'Upload failed');
                    }
                  }}
                  onRemove={(idx) => patchStep(c.id, si, {
                    screenshots: (s.screenshots || []).filter((_, k) => k !== idx),
                  })}
                />
              </div>
            ))}
            <button className="qa-btn qa-btn-ghost" onClick={() => addStep(c.id)}>+ Add step</button>
          </div>
        </div>
      ))}

      <button className="qa-btn qa-btn-ghost qa-add-case" onClick={addCase}>+ Add test case</button>

      {err && <ErrorNote message={err} />}

      <div className="qa-editor-foot">
        <button className="qa-btn" onClick={onBack}>Cancel</button>
        <button className="qa-btn qa-btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : `Save as v${nextVersion}`}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- versions admin

const VersionColumn = ({ title, hint, versions, onAdd, onRemove }) => {
  const [value, setValue] = useState('');
  return (
    <div className="qa-vcol">
      <div className="qa-vcol-head">
        <h3>{title}</h3>
        <span className="qa-muted">{hint}</span>
      </div>
      <div className="qa-vcol-list">
        {versions.map((v) => (
          <div className="qa-vrow" key={v}>
            <span>{v}</span>
            <button className="qa-x-small" onClick={() => onRemove(v)}>✕</button>
          </div>
        ))}
        {versions.length === 0 && <div className="qa-empty">No versions yet.</div>}
      </div>
      <div className="qa-vadd">
        <input
          type="text"
          placeholder="e.g. 3.4.3"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          className="qa-btn qa-btn-primary"
          onClick={() => {
            const v = value.trim();
            if (v) { onAdd(v); setValue(''); }
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
};

const VersionsScreen = ({ versions, onAdd, onRemove }) => (
  <div className="qa-screen">
    <div className="qa-muted qa-versions-intro">
      These lists feed the dropdowns when starting a new eval. Anyone with access can edit them.
    </div>
    <div className="qa-vcols">
      <VersionColumn
        title="App versions"
        hint="REKKIE app builds available to QA"
        versions={versions.appVersions}
        onAdd={(v) => onAdd('app', v)}
        onRemove={(v) => onRemove('app', v)}
      />
      <VersionColumn
        title="Goggles versions"
        hint="Goggles firmware builds available to QA"
        versions={versions.gogglesVersions}
        onAdd={(v) => onAdd('goggles', v)}
        onRemove={(v) => onRemove('goggles', v)}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------- root page

const QAValidationPage = () => {
  const { currentUser } = useAuth();
  const recordedAs = (currentUser && (currentUser.displayName || currentUser.email)) || 'you';

  const [tab, setTab] = useState('evals');
  const [evals, setEvals] = useState([]);
  const [suites, setSuites] = useState([]);
  const [versions, setVersions] = useState({ appVersions: [], gogglesVersions: [] });
  const [activeEval, setActiveEval] = useState(null);
  const [activeSuite, setActiveSuite] = useState(null);
  const [openEvalId, setOpenEvalId] = useState(null);
  const [openSuiteId, setOpenSuiteId] = useState(null);
  const [suiteMode, setSuiteMode] = useState('view');
  const [kickoffOpen, setKickoffOpen] = useState(false);
  const [caseRunner, setCaseRunner] = useState(null);

  const [listState, setListState] = useState({ loading: false, error: null });
  const [drillState, setDrillState] = useState({ loading: false, error: null });
  const [banner, setBanner] = useState(null);

  // ── loaders (stable identities for effect deps) ───────────────────
  const loadEvals = useCallback(async () => {
    setListState({ loading: true, error: null });
    try {
      const d = await api.listEvals();
      setEvals(d.evals);
      setListState({ loading: false, error: null });
    } catch (e) {
      setListState({ loading: false, error: e.message });
    }
  }, []);

  const loadSuites = useCallback(async () => {
    setListState({ loading: true, error: null });
    try {
      const d = await api.listSuites();
      setSuites(d.suites);
      setListState({ loading: false, error: null });
    } catch (e) {
      setListState({ loading: false, error: e.message });
    }
  }, []);

  const loadVersions = useCallback(async () => {
    try {
      setVersions(await api.getVersions());
    } catch (e) {
      setBanner(e.message);
    }
  }, []);

  const loadEval = useCallback(async (id) => {
    setDrillState({ loading: true, error: null });
    try {
      setActiveEval(await api.getEval(id));
      setDrillState({ loading: false, error: null });
    } catch (e) {
      setDrillState({ loading: false, error: e.message });
    }
  }, []);

  const loadSuite = useCallback(async (id) => {
    setDrillState({ loading: true, error: null });
    try {
      setActiveSuite(await api.getSuite(id));
      setDrillState({ loading: false, error: null });
    } catch (e) {
      setDrillState({ loading: false, error: e.message });
    }
  }, []);

  // ── effects ───────────────────────────────────────────────────────
  useEffect(() => { loadVersions(); }, [loadVersions]);

  useEffect(() => {
    if (tab === 'evals' && !openEvalId) loadEvals();
    if (tab === 'suites' && !openSuiteId) loadSuites();
  }, [tab, openEvalId, openSuiteId, loadEvals, loadSuites]);

  useEffect(() => {
    if (openEvalId) loadEval(openEvalId);
    else setActiveEval(null);
  }, [openEvalId, loadEval]);

  useEffect(() => {
    if (openSuiteId) loadSuite(openSuiteId);
    else setActiveSuite(null);
  }, [openSuiteId, loadSuite]);

  // ── navigation ────────────────────────────────────────────────────
  const goTab = (t) => {
    setTab(t);
    setOpenEvalId(null);
    setOpenSuiteId(null);
  };

  // ── mutations ─────────────────────────────────────────────────────
  const startEval = async (form) => {
    const created = await api.createEval(form);
    setKickoffOpen(false);
    setTab('evals');
    setOpenEvalId(created.id);
  };

  const toggleComplete = async () => {
    try {
      const next = activeEval.status === 'complete' ? 'in_progress' : 'complete';
      setActiveEval(await api.updateEval(activeEval.id, { status: next }));
    } catch (e) {
      setBanner(e.message);
    }
  };

  const saveCaseResult = async (payload) => {
    const updated = await api.saveCaseResult(caseRunner.evalId, caseRunner.caseId, payload);
    setActiveEval(updated);
    setCaseRunner(null);
  };

  const saveSuite = async (draft) => {
    await api.saveSuite(openSuiteId, draft);
    await loadSuite(openSuiteId);
    setSuiteMode('view');
  };

  const openSuite = (id) => {
    setOpenSuiteId(id);
    setSuiteMode('view');
  };

  const newSuite = async () => {
    try {
      const created = await api.createSuite({ name: 'New test suite', description: '', cases: [] });
      setOpenSuiteId(created.id);
      setSuiteMode('edit');
    } catch (e) {
      setBanner(e.message);
    }
  };

  const addVersion = async (kind, value) => {
    try {
      await api.addVersion(kind, value);
      await loadVersions();
    } catch (e) {
      setBanner(e.message);
    }
  };
  const removeVersion = async (kind, value) => {
    try {
      await api.removeVersion(kind, value);
      await loadVersions();
    } catch (e) {
      setBanner(e.message);
    }
  };

  // ── case runner inputs ────────────────────────────────────────────
  let runnerSuite = null;
  let runnerCase = null;
  if (caseRunner && activeEval) {
    for (const s of activeEval.suites) {
      const found = s.cases.find((c) => c.id === caseRunner.caseId);
      if (found) { runnerSuite = s; runnerCase = found; break; }
    }
  }

  const showDrill = openEvalId || openSuiteId;

  return (
    <div className="qa-eval-page">
      <div className="qa-header">
        <div>
          <h1>REKKIE Eval &amp; Test-Suites</h1>
          <p className="qa-header-sub">
            Structured manual QA of the app + goggles · all evals are shared
          </p>
        </div>
      </div>

      {banner && (
        <div className="qa-error qa-error-dismiss">
          <span>{banner}</span>
          <button className="qa-x" onClick={() => setBanner(null)}>✕</button>
        </div>
      )}

      {!showDrill && (
        <div className="qa-tabs">
          {[
            { id: 'evals', label: 'Evals' },
            { id: 'suites', label: 'Test Suites' },
            { id: 'versions', label: 'Versions' },
          ].map((t) => (
            <button
              key={t.id}
              className={`qa-tab ${tab === t.id ? 'qa-tab-on' : ''}`}
              onClick={() => goTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!showDrill && tab === 'evals' && (
        <EvalListScreen
          evals={evals}
          loading={listState.loading}
          error={listState.error}
          onOpenEval={setOpenEvalId}
          onStartEval={() => setKickoffOpen(true)}
        />
      )}
      {!showDrill && tab === 'suites' && (
        <SuitesScreen
          suites={suites}
          loading={listState.loading}
          error={listState.error}
          onOpenSuite={openSuite}
          onNewSuite={newSuite}
        />
      )}
      {!showDrill && tab === 'versions' && (
        <VersionsScreen versions={versions} onAdd={addVersion} onRemove={removeVersion} />
      )}

      {openEvalId && drillState.loading && (
        <div className="qa-screen">
          <button className="qa-back" onClick={() => setOpenEvalId(null)}>← All evals</button>
          <Spinner label="Loading eval…" />
        </div>
      )}
      {openEvalId && drillState.error && (
        <div className="qa-screen">
          <button className="qa-back" onClick={() => setOpenEvalId(null)}>← All evals</button>
          <ErrorNote message={drillState.error} />
        </div>
      )}
      {openEvalId && activeEval && !drillState.loading && (
        <EvalRunnerScreen
          run={activeEval}
          onBack={() => setOpenEvalId(null)}
          onOpenCase={(suiteId, caseId) => setCaseRunner({ evalId: activeEval.id, suiteId, caseId })}
          onToggleComplete={toggleComplete}
        />
      )}

      {openSuiteId && drillState.loading && (
        <div className="qa-screen">
          <button className="qa-back" onClick={() => setOpenSuiteId(null)}>← Test suites</button>
          <Spinner label="Loading suite…" />
        </div>
      )}
      {openSuiteId && activeSuite && !drillState.loading && suiteMode === 'view' && (
        <SuiteViewScreen
          suite={activeSuite}
          onBack={() => setOpenSuiteId(null)}
          onEdit={() => setSuiteMode('edit')}
        />
      )}
      {openSuiteId && activeSuite && !drillState.loading && suiteMode === 'edit' && (
        <SuiteEditorScreen
          suite={activeSuite}
          onBack={() => setSuiteMode('view')}
          onSave={saveSuite}
        />
      )}

      {kickoffOpen && (
        <KickoffModal
          appVersions={versions.appVersions}
          gogglesVersions={versions.gogglesVersions}
          onClose={() => setKickoffOpen(false)}
          onStart={startEval}
        />
      )}
      {caseRunner && runnerSuite && runnerCase && (
        <CaseRunnerPanel
          key={caseRunner.caseId}
          evalId={caseRunner.evalId}
          suite={runnerSuite}
          testCase={runnerCase}
          recordedAs={recordedAs}
          onClose={() => setCaseRunner(null)}
          onSave={saveCaseResult}
        />
      )}
    </div>
  );
};

export default QAValidationPage;
